import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from 'layout/Layout';
import {
    useGetProductBySlugQuery,
    useUpdateProductMutation,
    useDeleteProductMutation,
} from 'api/productApi';
import { productImageSrc } from 'utils/mediaUrl';

const ManageProduct = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const { data: product, isLoading } = useGetProductBySlugQuery(slug);
    const [updateProduct] = useUpdateProductMutation();
    const [deleteProduct] = useDeleteProductMutation();
    const [imagePreview, setImagePreview] = useState(null);

    const [formValues, setFormValues] = useState({
        name: '',
        description: '',
        category: '',
        price1: '',
        unit1: '',
        price2: '',
        unit2: '',
        price3: '',
        unit3: '',
        image: null,
    });

    useEffect(() => {
        if (product) {
            setFormValues({
                name: product.name || '',
                description: product.description || '',
                category: product.category || '',
                price1: product.price1 || '',
                unit1: product.unit1 || '',
                price2: product.price2 || '',
                unit2: product.unit2 || '',
                price3: product.price3 || '',
                unit3: product.unit3 || '',
                image: null,
            });
        }
    }, [product]);

    const handleChange = e => {
        const { name, value, files } = e.target;
        if (name === 'image') {
            const file = files[0];
            setFormValues(prev => ({ ...prev, image: file }));
            if (file) {
                setImagePreview(URL.createObjectURL(file));
            } else {
                setImagePreview(null);
            }
        } else {
            setFormValues(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdate = async e => {
        e.preventDefault();
        const formData = new FormData();
        for (const key in formValues) {
            if (formValues[key]) {
                formData.append(key, formValues[key]);
            }
        }
        try {
            await updateProduct({ slug, formData }).unwrap();
            alert('Product updated successfully.');
        } catch (error) {
            console.error('Update failed:', error);
            alert('Failed to update product.');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await deleteProduct(slug).unwrap();
                navigate('/product');
            } catch (error) {
                console.error('Delete failed:', error);
                alert('Failed to delete product.');
            }
        }
    };

    if (isLoading) return <p className="text-center mt-10">Loading...</p>;

    return (
        <Layout title="Manage Product" tagline="Update or delete your product">
            <form
                onSubmit={handleUpdate}
                className="mt-10 md:mt-16 mx-4 md:mx-auto max-w-4xl space-y-6 bg-white p-6 rounded shadow"
            >
                <h2 className="text-2xl font-semibold text-gray-800">Product Details</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block font-medium">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formValues.name}
                            onChange={handleChange}
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring"
                        />
                    </div>

                    <div>
                        <label className="block font-medium">Category</label>
                        <input
                            type="text"
                            name="category"
                            value={formValues.category}
                            onChange={handleChange}
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring"
                        />
                    </div>

                    <div className="sm:col-span-2">
                        <label className="block font-medium">Description</label>
                        <textarea
                            name="description"
                            value={formValues.description}
                            onChange={handleChange}
                            className="w-full border px-3 py-2 rounded focus:outline-none focus:ring"
                            rows="3"
                        />
                    </div>

                    {[1, 2, 3].map(num => (
                        <React.Fragment key={num}>
                            <div>
                                <label className="block font-medium">Price {num}</label>
                                <input
                                    type="number"
                                    name={`price${num}`}
                                    value={formValues[`price${num}`]}
                                    onChange={handleChange}
                                    className="w-full border px-3 py-2 rounded focus:outline-none focus:ring"
                                />
                            </div>
                            <div>
                                <label className="block font-medium">Unit {num}</label>
                                <input
                                    type="text"
                                    name={`unit${num}`}
                                    value={formValues[`unit${num}`]}
                                    onChange={handleChange}
                                    className="w-full border px-3 py-2 rounded focus:outline-none focus:ring"
                                />
                            </div>
                        </React.Fragment>
                    ))}

                    <div className="sm:col-span-2">
                        <label className="block font-medium">Product Image</label>
                        {(imagePreview || product?.image) && (
                            <img
                                src={imagePreview || productImageSrc(product)}
                                alt={`Cover for: ${product?.name || 'Product'}`}
                                className="w-full h-48 sm:h-64 md:h-80 object-cover rounded mb-2"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src =
                                        'data:image/svg+xml;charset=UTF-8,' +
                                        encodeURIComponent(
                                            `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"><rect fill="#f3f4f6" width="400" height="200"/><text fill="#6b7280" font-family="sans-serif" font-size="18" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Image unavailable</text></svg>`
                                        );
                                }}
                            />
                        )}
                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleChange}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-4">
                    <button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
                    >
                        Update Product
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition"
                    >
                        Delete Product
                    </button>
                </div>
            </form>
        </Layout>
    );
};

export default ManageProduct;
